import { locationIdFormat } from "@redotech/redo-inventory-id";
import { SuppliesTrackableProductTypeSchema } from "@redotech/redo-model/supplies/trackable-product";
import {
  productFamilyIdFormat,
  productFamilyOptionIdFormat,
  productFamilyOptionValueIdFormat,
  productIdFormat,
  productImageIdFormat,
} from "@redotech/redo-product-id";
import * as z from "zod";
import { RedoSuppliesProductFamilyImageSchema } from "./get-redo-supplies-products";

const RedoSuppliesProductImageSchema_ = z.object({
  id: productImageIdFormat.schema,
  url: z.string(),
  altText: z.string(),
});
export const RedoSuppliesProductImageSchema: z.ZodType<RedoSuppliesProductImage> =
  RedoSuppliesProductImageSchema_;
export interface RedoSuppliesProductImage extends z.infer<
  typeof RedoSuppliesProductImageSchema_
> {}

const RedoSuppliesOptionValueSchema_ = z.object({
  id: productFamilyOptionValueIdFormat.schema,
  value: z.string(),
});
export const RedoSuppliesOptionValueSchema: z.ZodType<RedoSuppliesOptionValue> =
  RedoSuppliesOptionValueSchema_;
export interface RedoSuppliesOptionValue extends z.infer<
  typeof RedoSuppliesOptionValueSchema_
> {}

const RedoSuppliesOptionSchema_ = z.object({
  id: productFamilyOptionIdFormat.schema,
  name: z.string(),
  values: z.array(RedoSuppliesOptionValueSchema),
});
export const RedoSuppliesOptionSchema: z.ZodType<RedoSuppliesOption> =
  RedoSuppliesOptionSchema_;
export interface RedoSuppliesOption extends z.infer<
  typeof RedoSuppliesOptionSchema_
> {}

const RedoSuppliesDetailProductSchema_ = z.object({
  id: productIdFormat.schema,
  price: z.number().nullable(),
  currency: z.string().nullable(),
  images: z.array(RedoSuppliesProductImageSchema),
  options: z.array(z.object({ name: z.string(), value: z.string() })),
  available: z.boolean(),
  maxQuantity: z.number().nullable(),
  hasInventoryConfig: z.boolean(),
  /** Set when the product can have inventory tracking set up. Nullish so a
   * pre-deploy server that omits it doesn't fail the client's parse. */
  trackable: z
    .object({
      type: SuppliesTrackableProductTypeSchema,
      quantityPerCase: z.number(),
    })
    .nullish(),
});
export const RedoSuppliesDetailProductSchema: z.ZodType<RedoSuppliesDetailProduct> =
  RedoSuppliesDetailProductSchema_;
export interface RedoSuppliesDetailProduct extends z.infer<
  typeof RedoSuppliesDetailProductSchema_
> {}

const RedoSuppliesProductDetailSchema_ = z.object({
  id: productFamilyIdFormat.schema,
  title: z.string(),
  description: z.string(),
  images: z.array(RedoSuppliesProductFamilyImageSchema),
  options: z.array(RedoSuppliesOptionSchema),
  products: z.array(RedoSuppliesDetailProductSchema),
});
export const RedoSuppliesProductDetailSchema: z.ZodType<RedoSuppliesProductDetail> =
  RedoSuppliesProductDetailSchema_;
export interface RedoSuppliesProductDetail extends z.infer<
  typeof RedoSuppliesProductDetailSchema_
> {}

export const getRedoSuppliesProductDetail = {
  name: "getRedoSuppliesProductDetail" as const,
  input: z.object({
    productFamilyId: productFamilyIdFormat.schema,
    destinationLocationId: locationIdFormat.schema.nullable().optional(),
  }),
  output: z.object({
    productFamily: RedoSuppliesProductDetailSchema.nullable(),
    hasLabelPaperConfigAtLocation: z.boolean(),
  }),
};
